import {useEffect, useRef, useState} from 'react'
import {updateFileTreeWidth, type FileTreeWidthContext} from '@github-ui/commits/shared/useUpdateUserTreePreferences'

export const STORAGE_KEY = 'diff-tree-pane-width'

// Pane width constraints (in pixels)
export const MIN_WIDTH = 256
export const DEFAULT_WIDTH = 296
export const MAX_WIDTH = 700

/**
 * Syncs the file tree pane width between server storage and localStorage.
 *
 * This hook does two things:
 * 1. On mount, syncs the server-provided width to localStorage (before Primer reads it)
 * 2. Intercepts localStorage writes to sync width changes back to the server
 *
 * @param serverWidth - The width value from the server (user settings)
 * @param context - The PR context (owner, repo, pullNumber) required for server sync
 */
export function useFileTreeWidthSync(serverWidth: number | undefined, context: FileTreeWidthContext | undefined): void {
  const lastSyncedWidthRef = useRef<number>(serverWidth ?? DEFAULT_WIDTH)

  useEffect(() => {
    if (serverWidth !== undefined) {
      lastSyncedWidthRef.current = serverWidth
    }
  }, [serverWidth])

  useState(() => {
    if (typeof window === 'undefined') return // SSR guard
    if (serverWidth !== undefined) {
      try {
        localStorage.setItem(STORAGE_KEY, String(serverWidth))
      } catch {
        // localStorage may not be available
      }
    }
  })

  // Intercept localStorage writes to sync width changes back to server
  useEffect(() => {
    if (!context) return

    const originalSetItem = localStorage.setItem.bind(localStorage)

    localStorage.setItem = (key: string, value: string) => {
      originalSetItem(key, value)

      if (key !== STORAGE_KEY) return

      const newWidth = parseInt(value, 10)
      if (isNaN(newWidth) || newWidth <= 0) return

      // Only sync to server if the width actually changed
      if (newWidth !== lastSyncedWidthRef.current) {
        lastSyncedWidthRef.current = newWidth
        updateFileTreeWidth(newWidth, context)
      }
    }

    return () => {
      localStorage.setItem = originalSetItem
    }
  }, [context])
}
